import React, { useState, useEffect, useCallback, useMemo } from "react";
import { createPortal } from "react-dom";
import { FaTimes, FaChevronRight, FaChevronLeft } from "react-icons/fa";
import "./HeroGalleryLightbox.css";

/**
 * Fullscreen image viewer — local UI only. No business logic.
 */
export default function HeroGalleryLightbox({
  open,
  onClose,
  images,
  startIndex = 0,
  onExit,
  hotelName = "",
}) {
  const list = Array.isArray(images) ? images.filter(Boolean) : [];
  const listKey = useMemo(() => list.join("\u0001"), [list]);
  const [viewIndex, setViewIndex] = useState(0);

  useEffect(() => {
    if (open && list.length > 0) {
      const i = Math.min(Math.max(0, startIndex), list.length - 1);
      setViewIndex(i);
    }
  }, [open, startIndex, list.length, listKey]);

  const close = useCallback(() => {
    if (typeof onExit === "function" && list.length > 0) {
      onExit(Math.min(viewIndex, list.length - 1));
    }
    onClose();
  }, [onClose, onExit, list.length, viewIndex]);

  useEffect(() => {
    if (!open) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        close();
      } else if (e.key === "ArrowLeft" && list.length > 1) {
        e.preventDefault();
        setViewIndex((v) => Math.max(0, v - 1));
      } else if (e.key === "ArrowRight" && list.length > 1) {
        e.preventDefault();
        setViewIndex((v) => Math.min(list.length - 1, v + 1));
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, list.length, close]);

  const canGoPrev = list.length > 1 && viewIndex > 0;
  const canGoNext = list.length > 1 && viewIndex < list.length - 1;

  const goPrev = () => {
    if (!canGoPrev) return;
    setViewIndex((v) => Math.max(0, v - 1));
  };

  const goNext = () => {
    if (!canGoNext) return;
    setViewIndex((v) => Math.min(list.length - 1, v + 1));
  };

  if (!open || list.length === 0 || typeof document === "undefined") {
    return null;
  }

  const src = list[viewIndex];
  const alt = hotelName ? `${hotelName} — תמונה ${viewIndex + 1}` : `תמונה ${viewIndex + 1}`;

  return createPortal(
    <div className="hero-gallery-lightbox" role="dialog" aria-modal="true" aria-label="צפייה בתמונות">
      <button
        type="button"
        className="hero-gallery-lightbox__backdrop"
        onClick={close}
        aria-label="סגור"
      />
      <div className="hero-gallery-lightbox__shell">
        <button
          type="button"
          className="hero-gallery-lightbox__close"
          onClick={close}
          aria-label="סגור"
        >
          <FaTimes aria-hidden />
        </button>
        <div className="hero-gallery-lightbox__stage">
          {list.length > 1 ? (
            <>
              <button
                type="button"
                className="hero-gallery-lightbox__nav hero-gallery-lightbox__nav--prev"
                onClick={goPrev}
                disabled={!canGoPrev}
                aria-label="התמונה הקודמת"
              >
                <FaChevronRight aria-hidden />
              </button>
              <button
                type="button"
                className="hero-gallery-lightbox__nav hero-gallery-lightbox__nav--next"
                onClick={goNext}
                disabled={!canGoNext}
                aria-label="התמונה הבאה"
              >
                <FaChevronLeft aria-hidden />
              </button>
            </>
          ) : null}
          <img
            key={src}
            src={src}
            alt={alt}
            className="hero-gallery-lightbox__img"
            decoding="async"
          />
        </div>
        {list.length > 1 ? (
          <p className="hero-gallery-lightbox__counter" aria-live="polite">
            {viewIndex + 1} / {list.length}
          </p>
        ) : null}
      </div>
    </div>,
    document.body
  );
}
